import { CommonModule, formatCurrency } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Inject,
  input,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { UNLIMITED_PASSES } from '@fantaz/shared/config/src/lib/app.constants';
import {
  BestScores,
  BuyPassRequest,
  CasualTournamentDetails,
  ConfiguredPasses,
  CurrentUser,
  EventSchedule,
  Game,
  PassUsage,
  TournamentPass,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { CurrencyModule } from '@fantaz/shared/ui/currency';
import { GamesUiGameDetailsModule } from '@fantaz/shared/ui/game-details';
import { calcCountdownContent } from '@fantaz/shared/utils/date';
import { getExperience } from '@fantaz/tournaments/data-access';
import { TournamentsUiPassCardListComponent } from '@fantaz/tournaments/ui/pass-card-list';
import { TournamentsUiScoreSummaryModule } from '@fantaz/tournaments/ui/score-summary';

@Component({
  selector: 'fz-tab-overview',
  standalone: true,
  imports: [
    CommonModule,
    TournamentsUiScoreSummaryModule,
    MatCardModule,
    CurrencyModule,
    TournamentsUiPassCardListComponent,
    MatIconModule,
    GamesUiGameDetailsModule,
  ],
  templateUrl: './tournament-overview.html',
  styleUrls: ['./tournament-overview.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TournamentOverview implements OnInit, AfterViewChecked {
  @Input()
  eventScheduleDetails: EventSchedule | null | undefined;

  @Input()
  eventCode?: string;

  @Input()
  gameInfo?: Game;

  @Input()
  user: CurrentUser | null | undefined;

  @Input()
  bestScores?: BestScores | undefined;

  @Input()
  prizeImage: string | undefined;

  @Input()
  eventPrototypeUuid: string | undefined;

  @Input()
  isLEP: boolean | undefined;

  /**
   * Tournament passes
   */
  @Input()
  tournamentPasses?: TournamentPass[];

  /**
   * Loaded state for the tournament passes
   */
  @Input()
  tournamentPassesLoaded: boolean;

  @Input()
  userPasses: UserPasses;

  details = input<CasualTournamentDetails | null | undefined>();

  @Output()
  share: EventEmitter<void> = new EventEmitter();

  @Output()
  sharePass: EventEmitter<any> = new EventEmitter();

  @Output()
  playGame: EventEmitter<TournamentPass | null> = new EventEmitter();

  @Output()
  buyPass: EventEmitter<BuyPassRequest> = new EventEmitter<BuyPassRequest>();

  @Output()
  tryForFree: EventEmitter<void> = new EventEmitter();

  @Input()
  casualGameEndTime: string | undefined;

  @Input()
  timesPlayed?: number | null;

  passesChanged = false;
  passesToShow: unknown[] = [];
  disableRemainingButton: boolean;
  tournamentClosesTomorrow: boolean;
  tournamentIsOverByTime: boolean;

  /**
   * Play control for the tournament
   */
  @Input()
  playControls: TemplateRef<unknown>;

  @ViewChild('passesSection')
  private passesSectionDiv: ElementRef<HTMLElement>;

  scrollDone = false;

  startDate = computed<Date | undefined>(() => {
    const details = this.details();

    if (details) {
      return typeof details.casualGameStartTime === 'string'
        ? new Date(details.casualGameStartTime)
        : details.casualGameStartTime;
    }

    return undefined;
  });

  endDate = computed<Date | undefined>(() => {
    const details = this.details();

    if (details) {
      return typeof details.casualGameEndTime === 'string'
        ? new Date(details.casualGameEndTime)
        : details.casualGameEndTime;
    }

    return undefined;
  });

  inTheFuture = computed(() => {
    const endDate = this.endDate();
    if (this.startDate()) {
      return this.startDate()!.getTime() > new Date().getTime();
    }
    return false;
  });

  constructor(
    private route: ActivatedRoute,
    @Inject(LOCALE_ID) public locale: string,
  ) {}

  ngOnInit() {
    const tournamentEndDate = new Date(this.casualGameEndTime as string);

    if (calcCountdownContent(tournamentEndDate).timeDifference < 0) {
      this.tournamentIsOverByTime = true;
    }
  }

  ngAfterViewChecked() {
    this.route.fragment.subscribe((fragment) => {
      if (fragment === 'passesSection') {
        if (this.passesSectionDiv && !this.scrollDone) {
          (this.passesSectionDiv.nativeElement as HTMLElement).scrollIntoView({
            behavior: 'smooth',
          });
          setTimeout(() => {
            this.scrollDone = true;
          }, 1000);
        }
      }
    });
  }
  /*
    This function returns the list of tags on the current page.
    If the tags are stored as a string, it splits the string into an array.
    Otherwise, it returns the array of tags.
  */
  get tags(): string[] {
    // Return an empty array if the details don't exist.
    const tags = this.details()?.metadata?.tags;
    if (!tags) return [];

    // Return the array of tags if they are already an array.
    if (Array.isArray(tags)) {
      return tags;
    }

    // Return the array of tags if they are a comma-separated string.
    if ('string' === typeof tags) {
      return (tags as string).split(',');
    }

    // Log an error if the tags are a different type.
    console.warn('Unable to parse tags.');
    return [];
  }

  onShare() {
    this.share.emit();
  }
  onPlay() {
    this.playGame.emit();
  }
  onTryForFree() {
    this.tryForFree.emit();
  }
  detDescription() {
    return this.details()?.metadata?.gameLongDescription !== '' &&
      this.details()?.metadata?.gameLongDescription !== undefined
      ? this.details()?.metadata?.gameLongDescription
      : this.gameInfo?.metadata?.longDescription;
  }

  isButtonDisabled(pass: TournamentPass) {
    return (
      (!pass.canBeBuy && !this.isActivePass(pass)) ||
      this.tournamentIsOverByTime ||
      this.details()?.isCompleted ||
      this.eventScheduleDetails?.retired ||
      this.maxEntriesReached ||
      this.buyingPass()
    );
  }

  get maxEntriesReached() {
    return (
      Number(this.details()?.timesCanEnter) - Number(this.timesPlayed) === 0
    );
  }

  get maxEntriesReachedTooltip() {
    if (this.maxEntriesReached) {
      return `This tournament allows ${
        this.details()?.timesCanEnter as number
      } ${
        (this.details()?.timesCanEnter as number) > 1 ? 'entries' : 'entry'
      }. You have reached the limit of how many times you can play in this tournament`;
    }
    return '';
  }

  isPay(pass: ConfiguredPasses | UserPasses): boolean {
    if (pass.purchased) {
      return true;
    }

    return false;
  }

  buyingPass() {
    return (
      this.tournamentPasses &&
      this.tournamentPasses?.findIndex((pass) => pass.processing === true) > -1
    );
  }

  clickBuyPassButton(pass: ConfiguredPasses | UserPasses) {
    if (this.isActivePass(pass)) {
      this.playGame.emit(pass as TournamentPass);
      return;
    }

    const request: BuyPassRequest = {
      eventPrototypeUuid: this.eventPrototypeUuid as string,
      fantazPassUuid: pass.uuid,
      tournamentName: this.details()?.name || '',
      gameName: this.details()?.game.name,
      eventScheduleUuid: this.eventScheduleDetails?.uuid as string,
      price: Number(pass.price.price),
      promoCodes: pass.price.promoCodes,
      passIndex: pass.passIndex,
    };
    this.buyPass.emit(request);
  }

  isActivePass(pass: ConfiguredPasses | UserPasses) {
    return 'activeCount' in pass;
  }

  calculatePercent(pass: ConfiguredPasses | UserPasses) {
    if (pass.entries >= 5000) {
      return null;
    }
    const passPrice = Number(pass.price.price);
    if (passPrice === 0) {
      return 100;
    }
    const amountWithoutPass =
      (this.eventScheduleDetails?.entryAmount || 0) * Number(pass.entries);
    return 100 - (Number(pass.price.price) / amountWithoutPass) * 100;
  }

  generateButtonText(pass: ConfiguredPasses | UserPasses) {
    const price = Number(pass.price.price);
    const priceFormatted = this.floorFigure(Number(pass.price.price));

    if (this.isActivePass(pass)) {
      const userPass: UserPasses = pass as UserPasses;
      const timesCanEnter = Number(userPass.entries);
      if (timesCanEnter >= UNLIMITED_PASSES) {
        return `Play - Unlimited`;
      }

      if (userPass.entries == 1) {
        return `Play - $${priceFormatted} USD`;
      }

      return `Play - ${
        timesCanEnter - Number(userPass.activeCount) + 1
      }/${timesCanEnter}`;
    }

    let prefix = 'Buy';

    if (pass.entries == 1 || (price == 0 && !(pass as any).purchased)) {
      prefix = 'Play -';
    }

    return `${prefix} $${priceFormatted} USD`;
  }

  getTournamentPromoCode(pass: ConfiguredPasses | UserPasses): string | null {
    if (pass.price.promoCodes && pass.price.promoCodes.length) {
      return `Code: ${pass.price.promoCodes[0].uuid}`;
    }
    return null;
  }

  getPassUsage(pass: ConfiguredPasses | UserPasses): PassUsage {
    let canBeUsed = true;
    let redeemDate = null;
    if (pass.price.promoCodes?.length && !this.isActivePass(pass)) {
      canBeUsed = !!pass.price.promoCodes[0]?.canBeUsed;
      redeemDate = pass.price.promoCodes[0]?.redeemDate;
    }
    return {
      canBeUsed,
      redeemDate,
    };
  }

  isAlreadyUsed(passUsage: PassUsage): boolean {
    return passUsage && !passUsage.canBeUsed;
  }

  floorFigure(figure: number): string {
    const decimals = 2;
    const d = Math.pow(10, decimals);
    return (parseInt(`${figure * d}`) / d).toFixed(decimals);
  }

  formatPassPrice(price: string): string | null {
    if (price) {
      const num = Number(price);
      const f = formatCurrency(
        parseFloat(this.floorFigure(num)),
        this.locale,
        '',
        '',
        '1.2-2',
      );
      return `$${f.toString()} USD`;
    }

    return '100% Free!';
  }

  generatePassContent(pass: ConfiguredPasses | UserPasses) {
    const price = this.formatPassPrice(pass.price.price);

    // !FIXME: Move it to a separate component
    let entries = `Valid for ${pass.entries} ${
      pass.entries > 1 ? 'entries' : 'entry'
    }`;
    if (pass.entries >= UNLIMITED_PASSES) {
      entries = 'Valid for unlimited entries';
    }
    if (this.isActivePass(pass)) {
      return `<div class='pass-content'>
          <span class="mat-headline-4 prize small-margin-top">
            ${price}
          </span>
          <div class="mat-body-1 small-margin-top" >${entries}</div>
          <div class='color-grey small-margin-top' fxFlex='100'> Purchased on ${this.formatDate(
            pass.createdAt,
          )}</div>
        </div>`;
    } else if (this.disableRemainingButton) {
      return `<div class='pass-content'>
          <span class="mat-headline-4 prize small-margin-top">
            ${price}
          </span>
          <div class="mat-body-1 small-margin-top">${entries}</div>
        </div>`;
    }

    return `<div class='pass-content'>
        <span class="mat-headline-4 prize small-margin-top">
          ${price}
        </span>
        <div class="mat-body-1 small-margin-top">${entries}</div>
      </div>`;
  }

  formatDate(date: Date) {
    return new Date(date).toLocaleString('en-us', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  onSharePromoCode(pass: any) {
    console.log('onSharePromoCode', pass);
    this.sharePass.emit(pass);
  }

  getGameInfo(): Game | null {
    if (this.gameInfo) {
      const metadata = Object.assign(
        {},
        this.gameInfo?.metadata || {},
        this.details()?.metadata || {},
      );
      return {
        ...this.gameInfo,
        metadata: {
          ...metadata,
          longDescription: this.detDescription(),
          experience: getExperience(this.details()),
        },
      };
    }
    return null;
  }
}
